/* PriceMatchModal - "Elders goedkoper?" modal that submits a price-match request
 * Updated: Email is now required. Tag icon is white. Submit button is sticky at the bottom of the modal.
 * Submits to the existing quote_requests table with request_type = 'price_match'.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader as Loader2, CircleCheck as CheckCircle, Tag } from 'lucide-react';

interface PriceMatchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productName: string;
  variantSize: string;
  variantType: string;
  variantId: string;
  ourPrice: number;
}

export default function PriceMatchModal({
  open,
  onOpenChange,
  productName,
  variantSize,
  variantType,
  variantId,
  ourPrice,
}: PriceMatchModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    competitor_url: '',
    competitor_price: '',
    quantity: '1',
    optional_message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Vul je naam in');
      return;
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Vul een geldig e-mailadres in');
      return;
    }

    if (!formData.competitor_url.trim()) {
      toast.error('Vul de link naar de goedkopere aanbieder in');
      return;
    }

    if (!formData.competitor_price.trim() || parseFloat(formData.competitor_price) <= 0) {
      toast.error('Vul een geldige prijs in');
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) < 1) {
      toast.error('Vul een geldig aantal in (minimaal 1)');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: quoteData, error } = await supabase
        .from('quote_requests')
        .insert({
          request_type: 'price_match',
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          plant_name: `${productName} ${variantSize} (${variantType})`,
          plant_height: variantSize,
          quantity: parseInt(formData.quantity),
          delivery_method: 'binnen_nl',
          postal_code: '-',
          preferred_contact_method: 'mail',
          optional_message: formData.optional_message.trim() || null,
          competitor_url: formData.competitor_url.trim(),
          competitor_price: parseFloat(formData.competitor_price),
          product_variant_id: variantId,
        })
        .select('id')
        .maybeSingle();

      if (error) {
        console.error('Error submitting price match request:', error);
        toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
        return;
      }

      setIsSuccess(true);
      toast.success('Prijsvergelijkingsaanvraag verzonden!');

      setFormData({
        name: '',
        email: '',
        phone: '',
        competitor_url: '',
        competitor_price: '',
        quantity: '1',
        optional_message: '',
      });
    } catch (error) {
      console.error('Error submitting price match request:', error);
      toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 overflow-hidden" aria-describedby={undefined}>
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-jv-primary mb-2">Aanvraag verzonden!</h2>
            <p className="text-gray-600 max-w-sm mb-6">
              We bekijken de prijs en nemen zo snel mogelijk contact met je op. Vaak binnen één werkdag.
            </p>
            <Button onClick={handleClose} className="bg-jv-primary hover:bg-jv-primary/90">
              Sluiten
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto px-6 pt-6 pb-2 flex-1">
              <DialogHeader className="mb-4">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-jv-secondary">
                  <Tag className="h-6 w-6 text-white" />
                </div>
                <DialogTitle className="text-2xl font-bold text-jv-primary">
                  Elders goedkoper?
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Geef ons de kans om de prijs te matchen. Vul onderstaand formulier in en we nemen contact met je op.
                </DialogDescription>
              </DialogHeader>

              <div className="mb-4 rounded-lg bg-jv-secondary/50 p-3 text-sm text-jv-dark">
                <p className="font-semibold">{productName} {variantSize}</p>
                <p className="text-jv-primary">Onze prijs: €{ourPrice.toFixed(2)} per stuk</p>
              </div>

              <form id="price-match-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pm_name" className="text-jv-dark font-semibold">
                    Naam <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pm_name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Je volledige naam"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pm_email" className="text-jv-dark font-semibold">
                      E-mail <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pm_email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="je.email@voorbeeld.nl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pm_phone" className="text-jv-dark font-semibold">
                      Telefoon <span className="text-gray-500 text-sm font-normal">(optioneel)</span>
                    </Label>
                    <Input
                      id="pm_phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="06 12345678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pm_url" className="text-jv-dark font-semibold">
                    Link naar goedkopere aanbieder <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pm_url"
                    type="url"
                    value={formData.competitor_url}
                    onChange={(e) => handleInputChange('competitor_url', e.target.value)}
                    placeholder="https://..."
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pm_price" className="text-jv-dark font-semibold">
                      Prijs bij concurrent <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pm_price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.competitor_price}
                      onChange={(e) => handleInputChange('competitor_price', e.target.value)}
                      placeholder="€0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pm_qty" className="text-jv-dark font-semibold">
                      Aantal <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="pm_qty"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                      placeholder="1"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pm_msg" className="text-jv-dark font-semibold">
                    Opmerking <span className="text-gray-500 text-sm font-normal">(optioneel)</span>
                  </Label>
                  <Textarea
                    id="pm_msg"
                    value={formData.optional_message}
                    onChange={(e) => handleInputChange('optional_message', e.target.value)}
                    placeholder="Eventuele aanvullende informatie"
                    rows={3}
                  />
                </div>
              </form>
            </div>

            <div className="border-t border-gray-200 bg-white px-6 py-4 shrink-0">
              <Button
                type="submit"
                form="price-match-form"
                disabled={isSubmitting}
                className="w-full bg-jv-accent font-bold text-white hover:bg-jv-accent/90"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Bezig met verzenden...
                  </>
                ) : (
                  'Verstuur aanvraag'
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
